import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { BrowserRouter } from 'react-router-dom';

import { setEcosystemBasePath } from '@/shared/services/axiosInstance';

/**
 * EcosystemContext: the client-side mirror of the server's universal tenant
 * resolver. On boot, EcosystemProvider determines which ecosystem (if any)
 * the current URL belongs to and exposes that record plus a `basePath`
 * helper so internal links and API calls stay in the right tenant namespace.
 *
 * Resolution modes:
 *   - subdomain : hostname matches <slug>.rileconnect.(org|dev)
 *   - path      : first non-empty URL path segment is the candidate slug
 *   - platform  : no candidate, or candidate is not a real ecosystem (404)
 */
const EcosystemContext = createContext();

// Matches <label>.rileconnect.(org|dev). The captured label is the
// candidate slug. 'www' is explicitly treated as the platform apex.
// rileconnect.com is deliberately not here: the server redirects it to the
// .org equivalent, so the address bar is never .com by the time this runs.
// Keep in step with server/src/middleware/tenant-resolution.js.
const SUBDOMAIN_RE = /^([a-z0-9-]+)\.rileconnect\.(org|dev)$/i;

/**
 * Inspect the current URL and produce a candidate slug + resolution mode.
 *
 * @returns {{ candidate: (string|null), mode: ('subdomain'|'path'|'platform') }}
 */
const resolveCandidate = () => {
    const { hostname, pathname } = window.location;

    // 1. Subdomain check.
    const subdomainMatch = hostname.match(SUBDOMAIN_RE);
    if (subdomainMatch) {
        const label = subdomainMatch[1].toLowerCase();
        if (label === 'www') {
            return { candidate: null, mode: 'platform' };
        }
        return { candidate: label, mode: 'subdomain' };
    }

    // 2. Path check: first non-empty path segment. Lowercased so a mixed-case
    // URL like `/CoStem/directory` resolves correctly AND the basePath we
    // propagate to BrowserRouter / setEcosystemBasePath uses the canonical
    // lowercase slug. Matches the subdomain branch above + the server-side
    // tenant-resolution middleware's path-mode lowercasing.
    const firstSegment = pathname.split('/').filter(Boolean)[0];
    if (firstSegment) {
        return { candidate: firstSegment.toLowerCase(), mode: 'path' };
    }

    // 3. No candidate.
    return { candidate: null, mode: 'platform' };
};

export const EcosystemProvider = ({ children }) => {
    const [ecosystem, setEcosystem] = useState(null);
    const [basePath, setBasePath] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const boot = async () => {
            const { candidate, mode } = resolveCandidate();

            // Platform mode (no candidate): skip the fetch entirely.
            if (!candidate) {
                if (!cancelled) {
                    setEcosystem(null);
                    setBasePath('');
                    setEcosystemBasePath('');
                    setLoading(false);
                }
                return;
            }

            try {
                // Platform-level endpoint: not tenant-scoped, takes the slug
                // from the URL param. basePath is still '' here, so the
                // axios interceptor (if this went through axios) would be a
                // no-op, but we use fetch directly so no auth/tenant
                // plumbing interferes with the boot call.
                const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
                const res = await fetch(
                    `${baseUrl}/api/ecosystems/by-slug/${encodeURIComponent(
                        candidate
                    )}`,
                    { credentials: 'include' }
                );

                if (cancelled) return;

                // Canonicalize the URL bar to the lowercase slug regardless
                // of resolution outcome. The `candidate` produced by
                // resolveCandidate is already lowercased (matching the
                // lowercase basePath we hand to BrowserRouter and the
                // server's tenant-resolution middleware), but if the user
                // landed on `/CoStem/directory` the URL bar still reads the
                // original mixed case. React Router v6's basename match is
                // case-sensitive: with `basename='/costem'` and pathname
                // `/CoStem/directory`, no route renders. replaceState
                // rewrites the visible URL without a navigation so
                // BrowserRouter, the address bar, and any future
                // copy/paste all use the canonical form.
                //
                // v1.0.1 #55: this block previously fired only inside the
                // `res.ok` branch, so mixed-case typo URLs that resolved to
                // 404 or hit a network error stayed in their typo form:
                // the user saw `/MisspelledTenant/...` in the address bar
                // and copy/paste perpetuated the typo. Now it runs in path
                // mode independent of the resolution outcome. Subdomain
                // canonicalisation is the server's job (CORS / hostname);
                // platform mode has no slug to rewrite.
                if (mode === 'path') {
                    const { pathname, search, hash } = window.location;
                    const segments = pathname.split('/');
                    // segments looks like ['', 'CoStem', 'directory']:
                    // the first non-empty segment is the slug. Only
                    // rewrite if it actually differs from the canonical
                    // form (avoids a no-op history mutation on the
                    // common all-lowercase case).
                    if (segments[1] && segments[1] !== candidate) {
                        segments[1] = candidate;
                        const canonicalPath = segments.join('/');
                        const canonicalUrl = canonicalPath + search + hash;
                        window.history.replaceState(
                            window.history.state,
                            '',
                            canonicalUrl
                        );
                    }
                }

                if (res.ok) {
                    // The candidate is a real ecosystem.
                    const record = await res.json();
                    if (cancelled) return;

                    // Path mode keeps the slug prefix; subdomain mode does not.
                    const resolvedBasePath =
                        mode === 'path' ? `/${candidate}` : '';

                    setEcosystem(record);
                    setBasePath(resolvedBasePath);
                    setEcosystemBasePath(resolvedBasePath);
                } else if (res.status === 404) {
                    // Candidate is not an ecosystem (e.g. a platform route
                    // like /about, or a bad URL): fall back to platform.
                    setEcosystem(null);
                    setBasePath('');
                    setEcosystemBasePath('');
                } else {
                    // Unexpected status: treat as a real error but still
                    // fall back to platform so the app remains usable.
                    setEcosystem(null);
                    setBasePath('');
                    setEcosystemBasePath('');
                    setError(
                        new Error(
                            `Ecosystem resolution failed with status ${res.status}`
                        )
                    );
                }
            } catch (err) {
                // Network failure (not a 404; 404 is normal). Fall back to
                // platform mode, canonicalize the URL bar (v1.0.1 #55) so a
                // mixed-case typo URL doesn't persist through reload, and
                // surface the error.
                if (cancelled) return;
                if (mode === 'path') {
                    const { pathname, search, hash } = window.location;
                    const segments = pathname.split('/');
                    if (segments[1] && segments[1] !== candidate) {
                        segments[1] = candidate;
                        const canonicalPath = segments.join('/');
                        const canonicalUrl = canonicalPath + search + hash;
                        window.history.replaceState(
                            window.history.state,
                            '',
                            canonicalUrl
                        );
                    }
                }
                setEcosystem(null);
                setBasePath('');
                setEcosystemBasePath('');
                setError(err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        boot();

        return () => {
            cancelled = true;
        };
    }, []);

    const value = useMemo(
        () => ({
            ecosystem,
            isPlatform: ecosystem === null,
            loading,
            error,
            slug: ecosystem ? ecosystem.slug : null,
            basePath,
            capabilities: ecosystem ? ecosystem.capabilities : null,
            taxonomy: ecosystem ? ecosystem.taxonomy : null,
        }),
        [ecosystem, loading, error, basePath]
    );

    return (
        <EcosystemContext.Provider value={value}>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </Spinner>
                </div>
            ) : (
                children
            )}
        </EcosystemContext.Provider>
    );
};

EcosystemProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

/**
 * Access the resolved ecosystem context.
 *
 * @returns {Object} The EcosystemContext value (ecosystem, isPlatform,
 *   loading, error, slug, basePath, capabilities, taxonomy).
 * @throws {Error} If used outside of an EcosystemProvider.
 */
export const useEcosystem = () => {
    const context = useContext(EcosystemContext);
    if (context === undefined) {
        throw new Error(
            'useEcosystem must be used within an EcosystemProvider'
        );
    }
    return context;
};

/**
 * Bridges the resolved ecosystem context to React Router. Reads the active
 * `basePath` and mounts a single `<BrowserRouter>` with `basename` set to it,
 * so every `<Link>` and `navigate()` call in the app auto-resolves under the
 * tenant prefix (e.g. `/costem/directory` in path mode) with no per-link
 * migration and no change to route definitions.
 *
 * EcosystemProvider renders a loading Spinner until resolution completes, so
 * EcosystemRouter only mounts once `basePath` is final: `<BrowserRouter>`
 * receives the correct `basename` on its first and only mount (no flicker,
 * no remount).
 *
 * `basePath` is `''` in subdomain/platform mode and `/<slug>` in path mode;
 * `basePath || '/'` maps the empty case to React Router's default basename.
 */
export const EcosystemRouter = ({ children }) => {
    const { basePath } = useEcosystem();

    return <BrowserRouter basename={basePath || '/'}>{children}</BrowserRouter>;
};

EcosystemRouter.propTypes = {
    children: PropTypes.node.isRequired,
};

export default EcosystemContext;
